import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LockIcon from '@mui/icons-material/Lock';
import { useTheme, alpha, Box } from '@mui/material';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useLicenseEntitlement } from '../../../../../hooks/useLicenseEntitlement';
import { useMenuItemStyles } from '../menuStyles/menuItemStyles';

import { IMenuItemProps } from './menuItemProps';

export function HeaderMenuItem(props: IMenuItemProps): React.ReactElement {
  const theme = useTheme();
  const styles = useMenuItemStyles(theme);
  const { checkFeature } = useLicenseEntitlement();
  const {
    openNewTab,
    title,
    absolutePath,
    doubleIndent,
    icon,
    relativePath,
    showToolTip,
    className,
    children,
    featureId,
    onLockedClick,
  } = props;

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLLIElement | null>(null);
  const hasChildren = !!children;

  // HA-136: Check if feature is available. The fallback conforms to the full
  // FeatureCheckResult shape (not just isAvailable) so both branches share one
  // type -- requiredTierName is only ever read below when isLocked is true,
  // which is only reachable via the checkFeature() branch, never this one.
  const featureCheck = featureId
    ? checkFeature(featureId)
    : { isAvailable: true, requiredTierName: '' };
  const isLocked = featureId ? !featureCheck.isAvailable : false;

  // Adjust tooltip for locked items
  const toolTipText = isLocked
    ? `Requires ${featureCheck.requiredTierName} tier to access`
    : showToolTip
      ? (props.toolTipText ?? title)
      : null;

  const rootClassName = className
    ? `menu-item ${styles.root} ${className}`
    : `menu-item ${styles.root}`;

  const key: string = props.key ?? `list-item:${title}`;

  interface IMenuItemLinkProps extends IMenuItemProps {
    name: React.ReactNode;
  }

  const MenuItemLink = (props: IMenuItemLinkProps) => {
    const { name, openNewTab, title, absolutePath, relativePath } = props;

    let href: string | undefined;

    if (relativePath && relativePath.length > 0) {
      href = `${relativePath}`;
    } else if (absolutePath) {
      return (
        <RouterLink
          key={key}
          aria-label={title}
          to={absolutePath}
          target={openNewTab ? '_blank' : undefined}
          className={styles.routerLink}
        >
          {name}
        </RouterLink>
      );
    }

    return (
      <RouterLink
        key={key}
        aria-label={title}
        to={href ?? ''}
        target={openNewTab ? '_blank' : undefined}
        className={styles.routerLink}
      >
        {name}
      </RouterLink>
    );
  };

  const textClassName = doubleIndent
    ? `${styles.headerMenuItemText} ${styles.indentedMenuItemText}`
    : styles.headerMenuItemText;

  const handleToggle = () => {
    // HA-136: Handle locked item clicks
    if (isLocked) {
      if (onLockedClick) {
        onLockedClick();
      }

      return;
    }

    if (!hasChildren) {
      return;
    }

    setOpen(!open);
  };

  // HA-136: Styles for locked menu items
  const lockedStyles = isLocked
    ? {
        opacity: 0.6,
        cursor: 'not-allowed',
        '&:hover': {
          backgroundColor: alpha(theme.palette.action.hover, 0.04),
        },
      }
    : {};

  return (
    <>
      <Tooltip title={toolTipText} placement="bottom-start">
        <MenuItem
          key={key}
          ref={anchorRef}
          className={rootClassName}
          onClick={handleToggle}
          sx={lockedStyles}
        >
          {icon && (
            <ListItemIcon className={styles.icon} sx={isLocked ? { opacity: 0.6 } : {}}>
              {icon}
            </ListItemIcon>
          )}
          <ListItemText className={`header-menu-item-text ${textClassName}`}>
            {/* HA-136: For locked items, don't render as link */}
            {isLocked ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                  {title}
                </Typography>
                <Chip
                  icon={<LockIcon sx={{ fontSize: 14 }} />}
                  label={featureCheck.requiredTierName}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    backgroundColor: alpha(theme.palette.warning.main, 0.15),
                    color: theme.palette.warning.dark,
                    '& .MuiChip-icon': {
                      color: theme.palette.warning.dark,
                      marginLeft: '4px',
                    },
                  }}
                />
              </Box>
            ) : (
              <MenuItemLink
                title={title}
                absolutePath={absolutePath}
                relativePath={relativePath}
                openNewTab={openNewTab}
                name={
                  <Typography variant="subtitle1">
                    {title}
                    {children && (
                      <FontAwesomeIcon
                        className={styles.subMenuChevron}
                        icon={open ? faChevronDown : faChevronRight}
                      />
                    )}
                  </Typography>
                }
              />
            )}
          </ListItemText>
        </MenuItem>
      </Tooltip>
      {children && !isLocked && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <MenuList className={`sub-menu-container ${styles.subMenu}`}>{children}</MenuList>
        </Collapse>
      )}
    </>
  );
}
